import React, { useEffect, useRef, useCallback } from 'react';

/**
 * Cloudflare Turnstile widget (explicit rendering).
 *
 * Loads the official Turnstile script once, renders a widget into a container
 * and reports the verification token via `onToken`. The token is single-use
 * and expires after ~5 minutes; on expiry/error we clear it via onToken('').
 *
 * Props:
 *  - siteKey: the public Turnstile site key (from /api/config)
 *  - onToken: (token: string) => void
 *  - action: optional string label (e.g. 'login' | 'register')
 *  - theme: 'auto' | 'light' | 'dark'
 */
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export const Turnstile = ({ siteKey, onToken, action = 'login', theme = 'auto' }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  const render = useCallback(() => {
    if (!containerRef.current || !window.turnstile || widgetIdRef.current) return;
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        theme,
        callback: (token) => onTokenRef.current && onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current && onTokenRef.current(''),
        'error-callback': () => onTokenRef.current && onTokenRef.current(''),
      });
    } catch (e) { /* render guarded */ }
  }, [siteKey, action, theme]);

  useEffect(() => {
    if (!siteKey) return undefined;
    let script = document.getElementById(SCRIPT_ID);
    if (window.turnstile) {
      render();
    } else if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', render);
      document.head.appendChild(script);
    } else {
      script.addEventListener('load', render);
    }
    const id = widgetIdRef.current;
    return () => {
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (e) { /* ignore */ }
      widgetIdRef.current = null;
      if (script) script.removeEventListener('load', render);
    };
  }, [siteKey, render]);

  if (!siteKey) return null;
  return (
    <div
      ref={containerRef}
      className="flex min-h-[65px] w-full items-center justify-center"
      aria-label="Cloudflare Sicherheitsprüfung"
      data-testid="turnstile-widget"
    />
  );
};

export default Turnstile;
